import { FC } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { QuestionnaireSchedule, QuestionnaireTimepoint } from '@stores/QuestionnaireAdminStore';
import { ScheduleTimingHelp } from './ScheduleTimingHelp';
import { useScheduleDialogController } from './useScheduleDialogController';

interface Props {
  open: boolean;
  // Null creates a new schedule.
  schedule: QuestionnaireSchedule | null;
  timepoints: QuestionnaireTimepoint[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Creates or edits a schedule. Once a cohort uses the schedule its code and timing are locked, since participants'
 * sittings already have their dates; only the name can change.
 */
export const ScheduleDialog: FC<Props> = ({ open, schedule, timepoints, onClose, onSaved }) => {
  const scheduleDialog = useScheduleDialogController(open, schedule, onSaved);
  const { edit, locked } = scheduleDialog;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{scheduleDialog.isNew ? 'New Schedule' : 'Edit Schedule'}</DialogTitle>
      <DialogContent>
        {locked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            A cohort uses this schedule, so only its name can change. For different timing, create a new schedule and
            move cohorts onto it in the cohort dialog.
          </Alert>
        )}
        {scheduleDialog.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {scheduleDialog.error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 3 }}>
          <TextField
            label="Name"
            value={edit.name}
            onChange={(e) => scheduleDialog.handleNameChange(e.target.value)}
            sx={{ flex: 2 }}
          />
          <TextField
            label="Code"
            value={edit.code}
            onChange={(e) => scheduleDialog.handleCodeChange(e.target.value)}
            disabled={locked}
            helperText="e.g. PROGRAM_12WK"
            sx={{ flex: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle1">Timepoints</Typography>
          <ScheduleTimingHelp />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each timepoint opens at midnight on the day of the cohort you enter, where day 1 is the start date. Leave Open
          for empty if it never closes.
        </Typography>
        {edit.rows.map((row, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              select
              label="Timepoint"
              value={row.timepointId ?? ''}
              onChange={(e) => scheduleDialog.handleRowChange(index, { timepointId: Number(e.target.value) })}
              disabled={locked}
              sx={{ flex: 2 }}
            >
              {timepoints.map((timepoint) => (
                <MenuItem key={timepoint.id} value={timepoint.id}>
                  {timepoint.name} ({timepoint.code})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Opens on day"
              type="number"
              value={row.opensOnDay}
              onChange={(e) => scheduleDialog.handleRowChange(index, { opensOnDay: e.target.value })}
              disabled={locked}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Open for (days)"
              type="number"
              value={row.windowDays}
              onChange={(e) => scheduleDialog.handleRowChange(index, { windowDays: e.target.value })}
              disabled={locked}
              placeholder="Never closes"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
            />
            <IconButton
              aria-label="Remove timepoint"
              onClick={() => scheduleDialog.handleRemoveRow(index)}
              disabled={locked}
            >
              <Delete />
            </IconButton>
          </Box>
        ))}
        {!locked && (
          <Button startIcon={<Add />} onClick={scheduleDialog.handleAddRow}>
            Add Timepoint
          </Button>
        )}
        {scheduleDialog.problems.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            To save: {scheduleDialog.problems.join('; ')}.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={scheduleDialog.saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={scheduleDialog.handleSave} disabled={!scheduleDialog.canSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
